import React, { useState, useEffect } from 'react';
import { Alert, Button, Box, IconButton } from '@mui/material';
import { Close as CloseIcon, Notifications as NotificationsIcon } from '@mui/icons-material';
import { useNotifications } from '../hooks/useNotifications';
import { useAuth } from '../contexts/auth-context';
import { useTranslation } from 'react-i18next';

export const NotificationPermissionBanner: React.FC = () => {
  const { t } = useTranslation();
  const { isSupported, permission, requestPermission, subscribe } = useNotifications();
  const { user } = useAuth();
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Afficher la bannière si:
    // - Les notifications sont supportées
    // - La permission n'a pas été accordée
    // - L'utilisateur n'a pas fermé la bannière
    const wasDismissed = localStorage.getItem('notificationBannerDismissed') === 'true';
    setDismissed(wasDismissed);
    setVisible(isSupported && permission === 'default' && !wasDismissed);
  }, [isSupported, permission]);

  const handleRequestPermission = async () => {
    console.log('[PUSH-PERMISSION] User clicked "Activer", requesting permission...', { userId: user?.id });
    const result = await requestPermission();
    console.log('[PUSH-PERMISSION] Permission result:', result);

    if (result === 'granted') {
      setVisible(false);
      // S'abonner aux notifications push après avoir obtenu la permission
      if (user?.id) {
        try {
          console.log('[PUSH-PERMISSION] Permission granted, subscribing user', user.id);
          await subscribe(user.id.toString());
          console.log('✅ [PUSH-PERMISSION] Abonnement push réussi pour l\'utilisateur', user.id);
        } catch (error) {
          console.error('❌ [PUSH-PERMISSION] Erreur lors de l\'abonnement push:', error);
        }
      } else {
        console.warn('[PUSH-PERMISSION] Permission granted but no user ID available');
      }
    } else if (result === 'denied') {
      console.log('[PUSH-PERMISSION] Permission denied by user');
      setVisible(false);
      setDismissed(true);
      localStorage.setItem('notificationBannerDismissed', 'true');
    }
  };

  const handleDismiss = () => {
    setVisible(false);
    setDismissed(true);
    localStorage.setItem('notificationBannerDismissed', 'true');
  };

  if (!visible) {
    return null;
  }

  return (
    <Box sx={{ position: 'fixed', top: 16, right: 16, left: 16, zIndex: 9999, maxWidth: 600, mx: 'auto' }}>
      <Alert
        severity="info"
        icon={<NotificationsIcon />}
        action={
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Button
              color="inherit"
              size="small"
              onClick={handleRequestPermission}
              variant="outlined"
            >
              {t('notifications.banner.enable')}
            </Button>
            <IconButton
              size="small"
              aria-label={t('common.close')}
              color="inherit"
              onClick={handleDismiss}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        }
      >
        {t('notifications.banner.message')}
      </Alert>
    </Box>
  );
};
